import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALL_TEMPLATE_READINESS,
  IMPLEMENTED_RESULT_TEMPLATES,
  TEMPLATE_READINESS,
} from '@lims/shared-types';
import { catalogueApi, type CatalogueItem } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Panel,
  SelectField,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';

const NONE = 'none';

/**
 * WHICH SPECIALISED TEMPLATE THIS TEST USES — the laboratory's own switch
 * (decision D238). Only an IMPLEMENTED template can be chosen (ASTM D2216,
 * D2726, D2041, AASHTO T85 and T96), and `ready` is refused by the API unless one is. Every
 * attempt keeps the template it started with (D239). A deployment never
 * changes this; the laboratory does, and the change is audited.
 */
export function SpecialisedTemplatePanel({ item }: { readonly item: CatalogueItem }) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const [template, setTemplate] = useState<string>(item.resultTemplateVersion ?? NONE);
  const [readiness, setReadiness] = useState<string>(item.templateReadiness);
  const [reason, setReason] = useState('');

  useEffect(() => {
    setTemplate(item.resultTemplateVersion ?? NONE);
    setReadiness(item.templateReadiness);
  }, [item.resultTemplateVersion, item.templateReadiness]);

  const save = useMutation({
    mutationFn: () =>
      catalogueApi.update(item.id, {
        templateReadiness: readiness,
        resultTemplateVersion: template === NONE ? null : template,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: async (next) => {
      queryClient.setQueryData(['catalogue', item.id], next);
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      setReason('');
      show({ tone: 'success', message: t('catalogue.templateSaved') });
    },
  });

  return (
    <Panel title={t('catalogue.templateTitle')} description={t('catalogue.templateHint')}>
      <div className="flex flex-col gap-stack" data-testid="specialised-template">
        <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
          <SelectField
            label={t('catalogue.templateLabel')}
            value={template}
            onChange={(value) => setTemplate(value ?? NONE)}
            options={[
              { value: NONE, label: t('catalogue.templateNone') },
              ...IMPLEMENTED_RESULT_TEMPLATES.map((key) => ({ value: key, label: t(`catalogue.templateKey.${key}`) })),
            ]}
          />
          <SelectField
            label={t('catalogue.readiness')}
            value={readiness}
            onChange={(value) => setReadiness(value ?? TEMPLATE_READINESS.NOT_READY)}
            options={ALL_TEMPLATE_READINESS.map((value) => ({
              value,
              label: t(`status.templateReadiness.${value}`),
            }))}
          />
        </div>
        {readiness === TEMPLATE_READINESS.READY && template !== NONE ? (
          <Alert tone="info" title={t('catalogue.templateReadyTitle')} description={t('catalogue.templateReadyBody')} />
        ) : null}
        <TextAreaField label={t('common.reasonOptional')} value={reason} onChange={setReason} rows={2} />
        <ActionBar>
          <ActionButton priority="secondary" isLoading={save.isPending} onPress={() => save.mutate()}>
            {t('catalogue.templateSave')}
          </ActionButton>
        </ActionBar>
        {save.error ? <ErrorNotice error={save.error} /> : null}
      </div>
    </Panel>
  );
}
